import { ID, IDPrefixes } from '@griply/types';
import { defaultTagColorOption } from '@griply/utils';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'shared/assets/icons';
import { TagSearchMenu } from 'shared/components/ui/tag-search-menu';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useSelectedRelationShipIdsById } from 'shared/hooks/use-relationships';

import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu';
import { useData } from '../../use-data';
import { MetaInteraction } from '../meta-interaction';

export type MetaTagsProps = {
  taskId?: ID;
  tagIds?: ID[];
  onChange: (relationTypeIds: ID[]) => void;
};

export const MetaTags: React.FC<MetaTagsProps> = ({
  taskId,
  tagIds,
  onChange,
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();
  const { t } = useTranslation();
  const { tags, createTag, relationships } = useData();
  const { tag: taskTagIds } = useSelectedRelationShipIdsById(
    taskId,
    IDPrefixes.Task,
    relationships,
  );

  const _tagIds = tagIds ?? taskTagIds;

  const _onCreate = async (name: string) => {
    if (!name || !createTag) {
      return;
    }
    const newTag = await createTag({
      name,
      color: defaultTagColorOption.value,
    });
    onChange([..._tagIds, newTag.id]);
  };

  const onShortcut = useCallback(
    (e: KeyboardEvent) => {
      // Don't trigger if Shift is pressed (as that might be another shortcut)
      if (e.shiftKey) {
        return;
      }

      if (!menuOpen) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        openMenu();
      }
    },
    [menuOpen, openMenu],
  );

  useKeysDown(['KeyT'], onShortcut, {
    ignoreInputFields: true,
    capture: true,
  });

  const tagLabels = useMemo(() => {
    const filteredTags = _tagIds?.length
      ? tags.filter((tag) => _tagIds?.includes(tag.id))
      : [];
    return filteredTags.length
      ? filteredTags.map(({ name }) => name).join(', ')
      : undefined;
  }, [_tagIds, tags]);

  const onClear = () => {
    onChange([]);
    closeMenu();
  };

  return (
    <>
      <MetaInteraction
        ref={buttonRef}
        icon={Tag}
        placeholder={t('dialogs.task-detail.meta.tags.placeholder')}
        label={tagLabels}
        onClick={openMenu}
        selected={menuOpen}
        onClear={tagLabels ? onClear : undefined}
      />

      {menuOpen && (
        <TagSearchMenu
          tags={tags}
          onChange={onChange}
          value={_tagIds}
          referenceElement={buttonRef}
          onCreate={createTag ? _onCreate : undefined}
          onOutside={closeMenu}
          hideCreateButton
        />
      )}
    </>
  );
};
